import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { Box, Grid, Text, TextArea, Select, FormField, ResponsiveContext, ThemeContext } from 'grommet';
import styled, { keyframes } from 'styled-components';
import Button from '../../../Button';
import InvoiceTag from '../../../InvoiceTag';
import CustomerTag from '../../../CustomerTag';
import PreviewPane from '../PreviewPane';
import DataTableWithBackendSearch from '../../../DataTableWithBackendSearch';
import InvoiceFilter from '../../../filters/InvoiceFilter';
import SearchCustomerFilter from '../../../search/filters/SearchCustomerFilter';
import TabToggle from '../../../TabToggle';
import FontAwesomeIcon from '../../../FontAwesomeIcon';
import MessageBanner from '../../../MessageBanner';
import KeyboardShortcut from '../../../KeyboardShortcut';
import { DataTablePreferenceContext } from '../../../DataTablePreferenceProvider';
import { NotificationsContext } from '../../../NotificationsProvider';

import { formatCurrency } from '../../../../utils/numbers';
import { formatDate } from '../../../../utils/dates';
import { getQueryString } from '../../../../utils/search';
import { getInvoiceStatus } from '../../../../utils/business';
import { getInvoiceStatusBadge, getOverdueRange } from '../../../../utils/invoice';
import {
    faSparkles as IconExtract,
    faXmarkLarge as IconClose,
    faCircleCheck as IconCheck,
    faChevronUp as IconChevronUp,
    faChevronDown as IconChevronDown,
    faTriangleExclamation as IconWarning,
    faWandMagicSparkles as IconWand,
} from '@fortawesome/pro-light-svg-icons';

const scanLine = keyframes`
    0% { top: 0%; }
    100% { top: 95%; }
`;

const ScanOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    overflow: hidden;
    background: rgba(59, 130, 246, 0.06);

    &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 3px;
        background: rgba(59, 130, 246, 0.8);
        box-shadow: 0 0 12px 4px rgba(59, 130, 246, 0.4), 0 0 30px 8px rgba(59, 130, 246, 0.15);
        animation: ${scanLine} 6s ease-in-out infinite;
    }
`;

/**
 * Call the backend to extract invoice identifiers.
 * If text is provided, extracts from text. Otherwise, OCRs the transaction's attachments.
 */
const extractFromBackend = async (transactionId, text) => {
    const body = text ? { text } : {};
    const response = await fetch(`/api/transactions/${transactionId}/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { error: true, status: data.status || response.status };
    }

    const data = await response.json();
    return { results: data.results || [] };
};

const getIdentifier = (inv) => inv.order?.identifier || inv.identifier || null;

const TAB_OPTIONS = [
    { name: 'Auto-match', value: 'paste' },
    { name: 'Browse', value: 'browse' },
];

const UnfoundItemRow = ({ item, allOpenInvoices, foundIds, onResolve, onRemove }) => {
    const [options, setOptions] = useState(allOpenInvoices);
    const [resolved, setResolved] = useState(null);

    const handleSearch = (text) => {
        const escapedText = text.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        const exp = new RegExp(escapedText, 'i');
        setOptions(allOpenInvoices.filter(inv => {
            const identifier = inv.order?.identifier || inv.identifier || '';
            return exp.test(identifier);
        }));
    };

    const handleSelect = ({ option }) => {
        setResolved(option);
        setTimeout(() => onResolve(item, option), 800);
    };

    if (resolved) {
        return (
            <Box
                direction="row"
                align="center"
                gap="small"
                flex={{ shrink: 0 }}
                animation={{ type: 'fadeIn', duration: 300 }}
            >
                <Text size="small" color="text-weak" style={{ textDecoration: 'line-through' }}>{item.text}</Text>
                <Box direction="row" gap="xxsmall" align="center">
                    <FontAwesomeIcon SelectedIcon={IconCheck} size="xsmall" color="green-strong" />
                    <InvoiceTag id={resolved.id} />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            direction="row"
            align="center"
            gap="small"
            flex={{ shrink: 0 }}
        >
            <Box width="150px" flex={{ shrink: 0 }}>
                <Text size="small" weight={500} truncate>{item.text}</Text>
            </Box>
            <Box width="200px" flex={{ shrink: 0 }}>
                <Select
                    size="small"
                    placeholder="Select invoice..."
                    options={options.filter(inv => !foundIds.has(inv.id))}
                    onChange={handleSelect}
                    onSearch={handleSearch}
                    labelKey={(inv) => inv.order?.identifier || inv.identifier || ''}
                    valueKey={{ key: 'id', reduce: true }}
                    dropHeight="small"
                >
                    {(option) => (
                        <Box pad={{ horizontal: 'small', vertical: 'xsmall' }} direction="row" justify="between" align="center">
                            <Text size="small">{option.order?.identifier || option.identifier}</Text>
                            <Text size="xsmall" color="text-weak">{formatCurrency(option.open_balance, option.currency)}</Text>
                        </Box>
                    )}
                </Select>
            </Box>
            <Button
                event={{ section: 'find_invoices_step', element: 'dismiss_unfound' }}
                plain
                onClick={() => onRemove(item)}
                label={<FontAwesomeIcon SelectedIcon={IconClose} size="xxsmall" color="text-weak" />}
            />
        </Box>
    );
};

const FindInvoicesStep = ({
    customerId,
    customerGroupId,
    transactionId,
    amountToMatch,
    previewSource,
    initialText = '',
    initialFoundInvoices = [],
    allOpenInvoices = [],
    isLoadingInvoices,
    onNext,
    onUploaded,
}) => {
    const size = useContext(ResponsiveContext);
    const theme = useContext(ThemeContext);
    const notificationCtx = useContext(NotificationsContext);
    const dataTablePreferenceCtx = useContext(DataTablePreferenceContext);
    const isMobile = size === 'small';

    const [activeTab, setActiveTab] = useState('paste');
    const [magicText, setMagicText] = useState(initialText);
    const [foundInvoices, setFoundInvoices] = useState(initialFoundInvoices);
    const [unfoundItems, setUnfoundItems] = useState([]);
    const [isExtracting, setIsExtracting] = useState(false);
    const [tableSelection, setTableSelection] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSelectedDrawer, setShowSelectedDrawer] = useState(false);
    const [noMatchMessage, setNoMatchMessage] = useState(null);
    const [selectedCustomers, setSelectedCustomers] = useState([]);

    const CombinedFilters = useCallback(({ filterValues, onApplyFilter, tab }) => (
        <Box direction="row" gap={{ column: 'small', row: 'small' }} wrap>
            <InvoiceFilter filterValues={filterValues} onApplyFilter={onApplyFilter} tab={tab} />
            {customerGroupId && (
                <SearchCustomerFilter
                    selectedItems={selectedCustomers}
                    onApplyFilter={setSelectedCustomers}
                    customerGroupId={customerGroupId}
                />
            )}
        </Box>
    ), [customerGroupId, selectedCustomers]);

    const foundTotal = useMemo(
        () => foundInvoices.reduce((sum, inv) => sum + parseFloat(inv.open_balance || 0), 0),
        [foundInvoices]
    );

    const foundIds = useMemo(
        () => new Set(foundInvoices.map(inv => inv.id)),
        [foundInvoices]
    );

    const hasText = magicText.trim().length > 0;
    const hasAttachments = !!previewSource;
    const canExtract = hasText || hasAttachments;

    const handleExtract = useCallback(async () => {
        if (!canExtract) return;
        setIsExtracting(true);
        setUnfoundItems([]);
        setNoMatchMessage(null);

        try {
            const response = await extractFromBackend(transactionId, hasText ? magicText.trim() : undefined);

            if (response.error) {
                if (response.status === 401) {
                    notificationCtx.showNotification({ status: 401, message: 'Session expired. Please sign in.' });
                }
                return;
            }

            const { results } = response;

            if (!results || results.length === 0) {
                setNoMatchMessage('No invoices detected. Try pasting different text or use the Browse tab to search manually.');
                setTimeout(() => setNoMatchMessage(null), 5000);
                return;
            }

            // Update textarea with cleaned-up identifiers
            setMagicText(results.map(r => r.identifier).join(' '));

            const matched = [];
            const unmatched = [];

            for (const { identifier, invoice_id } of results) {
                if (invoice_id) {
                    const inv = allOpenInvoices.find(i => String(i.id) === String(invoice_id));
                    if (inv && !foundIds.has(inv.id)) {
                        matched.push({
                            ...inv,
                            _identifier: getIdentifier(inv),
                            applied: parseFloat(inv.open_balance),
                        });
                    }
                } else {
                    // Check if this identifier matches an already-found invoice
                    const alreadyFound = foundInvoices.some(inv => {
                        const invIdentifier = getIdentifier(inv);
                        return invIdentifier && identifier &&
                            invIdentifier.toLowerCase() === identifier.toLowerCase();
                    });
                    if (!alreadyFound) {
                        unmatched.push({ text: identifier });
                    }
                }
            }

            if (matched.length === 0 && unmatched.length === 0) {
                const msg = results.length > 0
                    ? 'All detected invoices are already selected.'
                    : 'No matching open invoices found. Try the Browse tab to search manually.';
                setNoMatchMessage(msg);
                setTimeout(() => setNoMatchMessage(null), 5000);
            }

            setFoundInvoices(prev => [...prev, ...matched]);
            setUnfoundItems(unmatched);
        } catch (err) {
            console.error('Extract API failed:', err);
        } finally {
            setIsExtracting(false);
        }
    }, [canExtract, hasText, magicText, transactionId, allOpenInvoices, foundIds, foundInvoices]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (activeTab !== 'paste') return;
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                handleExtract();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeTab, handleExtract]);


    const handleRemoveFound = (invoiceId) => {
        setFoundInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    };

    const handleResolveUnfound = (item, invoice) => {
        setUnfoundItems(prev => prev.filter(u => u.text !== item.text));
        setFoundInvoices(prev => [...prev, {
            ...invoice,
            _identifier: getIdentifier(invoice),
            applied: parseFloat(invoice.open_balance),
        }]);
    };

    const handleAddFromTable = (invoices) => {
        const toAdd = (Array.isArray(invoices) ? invoices : [invoices])
            .filter(inv => !foundIds.has(inv.id));
        if (toAdd.length > 0) {
            setFoundInvoices(prev => [...prev, ...toAdd.map(inv => ({
                ...inv,
                _identifier: getIdentifier(inv),
                applied: parseFloat(inv.open_balance),
            }))]);
        }
        setTableSelection([]);
        setToggleCleared(prev => !prev);
    };

    const handleRemoveUnfound = (item) => {
        setUnfoundItems(prev => prev.filter(u => u.text !== item.text));
    };

    const hasRemittance = !!previewSource;
    const showPreviewPane = hasRemittance || !!transactionId;
    const defaultFilter = dataTablePreferenceCtx.getPreference({ table: 'invoices', tab: 'unpaid', key: 'filters' });

    const gridColumns = showPreviewPane && !isMobile ? ['550px', 'flex'] : ['flex'];
    const gridRows = ['flex', 'auto'];
    const gridAreas = showPreviewPane && !isMobile
        ? [
            { name: 'preview', start: [0, 0], end: [0, 0] },
            { name: 'content', start: [1, 0], end: [1, 0] },
            { name: 'footer', start: [0, 1], end: [1, 1] },
        ]
        : [
            { name: 'content', start: [0, 0], end: [0, 0] },
            { name: 'footer', start: [0, 1], end: [0, 1] },
        ];

    return (
        <Grid
            columns={gridColumns}
            rows={gridRows}
            areas={gridAreas}
            gap="small"
            style={{ height: 'calc(100vh - 80px)' }}
        >
            {/* Preview pane */}
            {showPreviewPane && !isMobile && (
                <Box gridArea="preview" style={{ position: 'relative' }}>
                    <PreviewPane size={size} pdfSource={previewSource} transactionId={transactionId} onUploaded={onUploaded}>
                        {isExtracting && <ScanOverlay />}
                        {hasRemittance && (
                            <Button
                                event={{ section: 'find_invoices_step', element: 'extract_from_attachment' }}
                                primary
                                disabled={isExtracting || isLoadingInvoices}
                                onClick={handleExtract}
                                tip="Auto-match from remittance"
                                icon={<FontAwesomeIcon SelectedIcon={IconWand} size="small" />}
                                style={{ position: 'absolute', bottom: '12px', right: '12px', zIndex: 1, borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                            />
                        )}
                    </PreviewPane>
                </Box>
            )}

            {/* Right side — scrollable content */}
            <Box gridArea="content" overflow="auto" gap="small">
                {/* Mobile preview */}
                {showPreviewPane && isMobile && (
                    <PreviewPane size={size} pdfSource={previewSource} transactionId={transactionId} onUploaded={onUploaded}>
                        {isExtracting && <ScanOverlay />}
                    </PreviewPane>
                )}

                {/* Tab toggle */}
                <Box direction="row" align="center" gap="small" flex={{ shrink: 0 }}>
                    <TabToggle
                        name="find-invoices-mode"
                        options={TAB_OPTIONS}
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                    />
                </Box>

                {/* Browse tab — invoice data table */}
                {activeTab === 'browse' && (
                    <Box flex>
                        <DataTableWithBackendSearch
                            key={`invoices-${foundIds.size}`}
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 300px)"
                            pageSize={50}
                            pageSizeOptions={[50, 100, 200]}
                            filterParams={{ defaultFilter }}
                            filterControls={customerGroupId ? CombinedFilters : InvoiceFilter}
                            emptyListMessage="No invoices found"
                            dataTableWrapperProps={{ margin: '0', pad: '0' }}
                            dataName="invoices"
                            columns={[
                                {
                                    selector: row => row.id,
                                    name: 'Invoice #',
                                    cell: (datum) => <InvoiceTag id={datum.id} />,
                                    sortField: 'identifier',
                                    sortable: true,
                                    minWidth: '140px',
                                    grow: 1.2,
                                },
                                {
                                    selector: row => row.issued_at,
                                    name: 'Issued',
                                    cell: (datum) => formatDate(datum.raw_issued_at),
                                    sortField: 'issued_at',
                                    sortable: true,
                                },
                                !isMobile && customerGroupId && {
                                    selector: row => row.recipient_id,
                                    name: 'Customer',
                                    cell: (datum) => datum.recipient_id && <CustomerTag id={datum.recipient_id} inline />,
                                },
                                !isMobile && {
                                    selector: row => row.invoice_status,
                                    name: 'Status',
                                    cell: (datum) => getInvoiceStatusBadge({ ...datum, theme }),
                                },
                                !isMobile && {
                                    selector: row => row.due_date,
                                    name: 'Due',
                                    cell: (datum) => formatDate(datum.raw_due_date),
                                    sortField: 'due_date',
                                    sortable: true,
                                },
                                !isMobile && {
                                    selector: row => row.amount,
                                    name: 'Amount',
                                    right: true,
                                    cell: (datum) => formatCurrency(datum.amount, datum.currency),
                                    sortField: 'amount',
                                    sortable: true,
                                },
                                {
                                    selector: row => row.open_balance,
                                    name: 'Balance',
                                    right: true,
                                    cell: (datum) => formatCurrency(datum.open_balance, datum.currency),
                                    sortField: 'open_balance',
                                    sortable: true,
                                },
                            ].filter(Boolean)}
                            reshape={(data) => {
                                if (!data) return null;
                                return data.invoices
                                    .filter(invoice => !foundIds.has(invoice.id))
                                    .map((invoice) => ({
                                        ...invoice,
                                        amount: parseFloat(invoice.amount),
                                        open_balance: parseFloat(invoice.open_balance),
                                        raw_issued_at: invoice.issued_at,
                                        issued_at: new Date(invoice.issued_at),
                                        raw_due_date: invoice.due_date,
                                        due_date: new Date(invoice.due_date),
                                        invoice_status: getInvoiceStatus(invoice.due_date, invoice.open_balance),
                                        overdue_range: getOverdueRange(new Date(invoice.due_date)),
                                    }));
                            }}
                            refetchUrlIfTheseParamsChange={{ selectedCustomers }}
                            getUrl={({ filterQuery, ...rest }) => {
                                const customerFilter = selectedCustomers.length > 0
                                    ? selectedCustomers.map(item => {
                                        const type = item._type === 'group' ? 'group' : 'customer';
                                        return `${type}:${item.id}`;
                                    }).join(',')
                                    : null;
                                const params = {
                                    invoice_state: (filterQuery || defaultFilter || []).join(','),
                                    status: 'all',
                                    ...(customerFilter
                                        ? { customer_filter: customerFilter }
                                        : {
                                            ...(customerId && { customer_id: customerId }),
                                            ...(customerGroupId && { include_group_invoices: 1 }),
                                        }
                                    ),
                                };
                                return getQueryString({
                                    ...rest,
                                    baseUrl: 'api/invoices',
                                    optionalParams: params,
                                });
                            }}
                            selectableRows
                            clearSelectedRows={toggleCleared}
                            onSelectedRowsChange={(data) => data?.selectedRows && setTableSelection(data.selectedRows)}
                            rowName={{ single: 'invoice', plural: 'invoices' }}
                            selectedControls={
                                <Box direction="row" gap="small" align="center">
                                    {amountToMatch && (
                                        <Text color="text-weak" size="small">
                                            {formatCurrency(tableSelection.reduce((sum, inv) => sum + parseFloat(inv.open_balance || 0), 0))}
                                            {` of ${formatCurrency(amountToMatch - foundTotal)}`}
                                        </Text>
                                    )}
                                    <Button
                                        event={{ section: 'find_invoices_step', element: 'add_selected' }}
                                        primary
                                        size="small"
                                        onClick={() => handleAddFromTable(tableSelection)}
                                        label={`Add ${tableSelection.length} invoice${tableSelection.length !== 1 ? 's' : ''}`}
                                    />
                                </Box>
                            }
                            hideExport
                        />
                    </Box>
                )}

                {/* Auto-match tab */}
                {activeTab === 'paste' && (
                    <Box gap="small" flex>
                        <Text size="small" color="text-weak">
                            Paste remittance or payment details. We&apos;ll automatically find and match the open invoices.
                        </Text>
                        <Box gap="xsmall">
                            <FormField htmlFor="magic-text" margin="none">
                                <TextArea
                                    id="magic-text"
                                    placeholder="#INV-001, PO-123, 4500..."
                                    value={magicText}
                                    onChange={(e) => setMagicText(e.target.value)}
                                    disabled={isExtracting}
                                    rows={6}
                                    resize="vertical"
                                    plain
                                />
                            </FormField>
                            <Box direction="row" gap="small" align="center">
                                <Button
                                    event={{ section: 'find_invoices_step', element: 'extract' }}
                                    primary
                                    disabled={!hasText || isExtracting || isLoadingInvoices}
                                    onClick={handleExtract}
                                    label={
                                        <Box direction="row" gap="xsmall" align="center">
                                            <FontAwesomeIcon SelectedIcon={IconExtract} size="xsmall" color="text-high-contrast" />
                                            <Text>{isExtracting ? 'Matching...' : 'Auto-match'}</Text>
                                            {!isMobile && <KeyboardShortcut keys={['Cmd', 'Enter']} variant="primary" />}
                                        </Box>
                                    }
                                />
                                {(magicText.trim() || unfoundItems.length > 0) && !isLoadingInvoices && (
                                    <Button
                                        event={{ section: 'find_invoices_step', element: 'clear_text' }}
                                        onClick={() => { setMagicText(''); setUnfoundItems([]); }}
                                        label="Clear"
                                    />
                                )}
                                {isLoadingInvoices && (
                                    <Text color="text-weak">Loading invoices...</Text>
                                )}
                            </Box>
                        </Box>

                        {noMatchMessage && (
                            <MessageBanner
                                compact
                                icon={IconWarning}
                                color="status-warning"
                                message={noMatchMessage}
                            />
                        )}

                        {/* Not found items */}
                        {unfoundItems.length > 0 && (
                            <Box gap="xsmall" border={{ side: 'top' }} pad={{ top: 'small' }}>
                                <Text weight={500} color="status-warning" style={{ flexShrink: 0 }}>
                                    {unfoundItems.length} item{unfoundItems.length !== 1 ? 's' : ''} not found
                                </Text>
                                <Box overflow={{ vertical: 'auto' }} height={{ max: '250px' }} gap="xxsmall">
                                    {unfoundItems.map((item) => (
                                        <UnfoundItemRow
                                            key={item.text}
                                            item={item}
                                            allOpenInvoices={allOpenInvoices}
                                            foundIds={foundIds}
                                            onResolve={handleResolveUnfound}
                                            onRemove={handleRemoveUnfound}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Selection bar — spans full width across both columns */}
            <Box
                gridArea="footer"
                background="foreground"
                border={foundInvoices.length > 0 ? { side: 'top' } : undefined}
                pad={foundInvoices.length > 0 ? { horizontal: 'small', vertical: isMobile ? 'medium' : 'small' } : undefined}
                gap={isMobile ? 'small' : 'xsmall'}
            >
                {foundInvoices.length > 0 && (
                    <Box gap={isMobile ? 'small' : 'xsmall'} animation={{ type: 'fadeIn', duration: 200 }}>
                        <Box direction="row" gap="small" align="center">
                            <Button
                                event={{ section: 'find_invoices_step', element: 'toggle_selected' }}
                                plain
                                onClick={() => setShowSelectedDrawer(prev => !prev)}
                                label={
                                    <Box direction="row" gap="xsmall" align="center">
                                        <Text weight={500} style={{ textDecoration: 'underline' }}>
                                            {foundInvoices.length} invoice{foundInvoices.length !== 1 ? 's' : ''} selected
                                        </Text>
                                        <FontAwesomeIcon SelectedIcon={showSelectedDrawer ? IconChevronDown : IconChevronUp} size="xxsmall" color="text-weak" />
                                    </Box>
                                }
                            />
                            {!isMobile && (amountToMatch && foundTotal === amountToMatch ? (
                                <Box direction="row" gap="xsmall" align="center">
                                    <FontAwesomeIcon SelectedIcon={IconCheck} size="small" color="green-strong" />
                                    <Text weight={500} color="green-strong">Perfect match</Text>
                                </Box>
                            ) : (
                                <Text color="text-weak">
                                    {formatCurrency(foundTotal)}
                                    {amountToMatch ? ` of ${formatCurrency(amountToMatch)}` : ''}
                                </Text>
                            ))}
                            <Button
                                event={{ section: 'find_invoices_step', element: 'clear_selection' }}
                                plain
                                onClick={() => { setFoundInvoices([]); setShowSelectedDrawer(false); }}
                                label={<Text color="text-weak">Clear</Text>}
                            />
                            {!isMobile && (
                                <Box flex align="end">
                                    <Button
                                        event={{ section: 'find_invoices_step', element: 'continue' }}
                                        primary
                                        onClick={() => onNext(foundInvoices, unfoundItems)}
                                        label="Continue"
                                    />
                                </Box>
                            )}
                        </Box>
                        {isMobile && (amountToMatch && foundTotal === amountToMatch ? (
                            <Box direction="row" gap="xsmall" align="center">
                                <FontAwesomeIcon SelectedIcon={IconCheck} size="small" color="green-strong" />
                                <Text weight={500} color="green-strong">Perfect match</Text>
                            </Box>
                        ) : (
                            <Text color="text-weak">
                                {formatCurrency(foundTotal)}
                                {amountToMatch ? ` of ${formatCurrency(amountToMatch)}` : ''}
                            </Text>
                        ))}
                        {isMobile && (
                            <Button
                                event={{ section: 'find_invoices_step', element: 'continue' }}
                                primary
                                fill="horizontal"
                                onClick={() => onNext(foundInvoices, unfoundItems)}
                                label="Continue"
                            />
                        )}
                    </Box>
                )}
                {showSelectedDrawer && foundInvoices.length > 0 && (
                    <Box
                        direction="row"
                        wrap
                        gap={{ row: 'xxsmall', column: 'xxsmall' }}
                        overflow={{ vertical: 'auto' }}
                        height={{ max: '180px' }}
                        pad="xsmall"
                        round="xsmall"
                        background="background-contrast"
                    >
                        {foundInvoices.map(inv => (
                            <InvoiceTag key={inv.id} id={inv.id} onRemove={handleRemoveFound} />
                        ))}
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

export default FindInvoicesStep;
